import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { COLORS, CARD_PAD } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";

/**
 * Başlıklı section kartı. Detail/settings sayfaları ve dashboard bölümleri için.
 *
 * `icon` verilmediğinde kompakt detay-sayfası görünümü korunur (mevcut tüm
 * çağıranlar bu yolda). `icon` verildiğinde başlık şeridi dashboard diline
 * geçer: gradient ikon çipi + büyük başlık + alt açıklama.
 *
 * @param {string} title - Bölüm başlığı
 * @param {string} [subtitle] - Başlığın altındaki açıklama (sadece icon ile)
 * @param {React.ReactNode} [icon] - Başlık çipinin içindeki ikon
 * @param {string} [accent] - İkon çipinin rengi (varsayılan: tema primary)
 * @param {React.ReactNode} [action] - Sağ üst aksiyon (kaydet butonu vs.)
 * @param {React.ReactNode} children
 */
export default function SectionCard({
  title,
  subtitle,
  icon,
  accent,
  action,
  children,
  sx = {},
}) {
  const { palette } = useClinicTheme();
  const tone = accent || palette.primary;
  const hasIcon = Boolean(icon);

  return (
    <Box
      sx={{
        border: `1px solid ${COLORS.slate200}`,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: COLORS.white,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: CARD_PAD,
          py: hasIcon ? 1.75 : 1.25,
          bgcolor: COLORS.slate50,
          borderBottom: `1px solid ${COLORS.slate100}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          {hasIcon ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                p: 1,
                borderRadius: 2,
                color: tone,
                background: `linear-gradient(135deg, ${alpha(tone, 0.1)} 0%, ${alpha(tone, 0.04)} 100%)`,
                border: `1px solid ${alpha(tone, 0.18)}`,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: hasIcon ? "0.95rem" : "0.8rem",
                color: hasIcon ? COLORS.slate800 : COLORS.slate700,
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            {hasIcon && subtitle ? (
              <Typography
                sx={{ fontSize: "0.75rem", color: COLORS.slate500, mt: 0.25 }}
                noWrap
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {action}
      </Box>
      {children}
    </Box>
  );
}
